import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { api, formatBRL } from "../lib/api";
import { toast } from "sonner";
import {
  CalendarClock, Loader2, RefreshCcw, TrendingUp, TrendingDown,
  Coins, Wallet, RotateCcw, AlertTriangle,
  LifeBuoy, Percent, Landmark, ArrowDownRight, ArrowUpRight, Minus,
} from "lucide-react";

const PERIODS = [
  { v: "hoje", l: "Hoje" },
  { v: "semana", l: "Semana atual" },
  { v: "mes_atual", l: "Mês atual" },
  { v: "mes_anterior", l: "Mês anterior" },
  { v: "30d", l: "Últimos 30 dias" },
  { v: "custom", l: "Personalizado" },
];

/* Linha do demonstrativo (label + valor + eventual sub-label) */
function StmtRow({ label, value, icon: Icon, hint, accent = "neutral", indent = 0, testId }) {
  const cls = {
    gold: "text-[#D4AF37]",
    emerald: "text-emerald-300",
    red: "text-red-300",
    amber: "text-amber-300",
    cyan: "text-cyan-300",
    neutral: "text-[#E5E5E5]",
  }[accent] || "text-[#E5E5E5]";
  return (
    <div
      className="flex items-baseline justify-between border-b border-dashed border-[#1a1a1a] py-2.5"
      style={{ paddingLeft: indent * 14 }}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon size={13} className={`${cls} opacity-70 flex-shrink-0`}/>}
        <div className="text-[13px] text-[#D0D0D0] truncate">{label}</div>
        {hint && <div className="text-[10.5px] text-[#737373] hidden sm:block">— {hint}</div>}
      </div>
      <div className={`font-mono-tab text-[13.5px] whitespace-nowrap font-medium ${cls}`}>{value}</div>
    </div>
  );
}

/* Cabeçalho de seção do demonstrativo */
function StmtSection({ title, subtotal, subtotalAccent = "neutral", subtotalIcon: Icon, children, testId }) {
  const cls = {
    emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
    red: "text-red-300 border-red-500/30 bg-red-500/5",
    gold: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/5",
    amber: "text-amber-300 border-amber-500/30 bg-amber-500/5",
    cyan: "text-cyan-300 border-cyan-500/30 bg-cyan-500/5",
    neutral: "text-[#A3A3A3] border-[#262626] bg-[#0d0d0d]",
  }[subtotalAccent] || "text-[#A3A3A3] border-[#262626] bg-[#0d0d0d]";
  return (
    <div className="mb-5 rounded-md border border-[#1f1f1f] bg-[#0a0a0a] overflow-hidden" data-testid={testId}>
      <div className={`px-4 py-2.5 border-b flex items-center justify-between ${cls}`}>
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold flex items-center gap-2">
          {Icon && <Icon size={13}/>}
          {title}
        </div>
        {subtotal !== undefined && (
          <div className="font-mono-tab text-[13px] font-bold">{subtotal}</div>
        )}
      </div>
      <div className="px-4 pt-1 pb-2">
        {children}
      </div>
    </div>
  );
}

export default function FechamentoPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("mes_atual");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === "custom") {
        if (customStart) params.start_date = customStart;
        if (customEnd) params.end_date = customEnd;
      }
      const { data } = await api.get("/reports/period-results", { params });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar fechamento");
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => { if (period !== "custom") load(); }, [period, load]);

  const lucroLiquido = data?.lucro_liquido ?? 0;
  const lucroSign = lucroLiquido > 0 ? "positive" : lucroLiquido < 0 ? "negative" : "zero";

  return (
    <Layout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-300">Demonstrativo do período</div>
          <h1 className="font-display text-3xl sm:text-[34px] font-bold tracking-tight mt-1" data-testid="fechamento-title">
            Fechamento Mensal
          </h1>
          <p className="text-[#A3A3A3] text-[13px] mt-1.5 max-w-2xl">
            Estruturado como demonstrativo financeiro — <strong className="text-emerald-300">receitas, movimento de capital, inadimplência e despesas</strong>, consolidado em um único resultado do período.
          </p>
        </div>
      </div>

      {/* Filtros de período */}
      <div className="fc-card p-4 mb-6 flex flex-wrap gap-2 items-center" data-testid="fechamento-filters">
        {PERIODS.map(p => (
          <button
            key={p.v}
            onClick={() => setPeriod(p.v)}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              period === p.v
                ? "border-emerald-500/50 text-emerald-200 bg-emerald-500/10"
                : "border-[#1f1f1f] text-[#A3A3A3] hover:bg-[#0f0f0f]"
            }`}
            data-testid={`period-${p.v}`}
          >
            {p.l}
          </button>
        ))}
        {period === "custom" && (
          <div className="flex gap-1.5 items-center flex-wrap">
            <input type="date" className="fc-input text-[12px]" value={customStart} onChange={e => setCustomStart(e.target.value)} data-testid="custom-start"/>
            <span className="text-[#737373] text-[12px]">até</span>
            <input type="date" className="fc-input text-[12px]" value={customEnd} onChange={e => setCustomEnd(e.target.value)} data-testid="custom-end"/>
            <button onClick={load} className="fc-btn-ghost text-xs" data-testid="custom-apply">Aplicar</button>
          </div>
        )}
        <button onClick={load} disabled={loading} className="fc-btn-ghost text-xs flex items-center gap-1.5 sm:ml-auto" data-testid="btn-reload">
          {loading ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
          Atualizar
        </button>
      </div>

      {data && (
        <>
          {/* Header do período */}
          <div className="mb-5 flex items-center gap-2 text-[12px] text-[#A3A3A3]" data-testid="period-label">
            <CalendarClock size={14} className="text-emerald-300"/>
            <span>
              {data.period.label} — <span className="font-mono-tab text-[#D4AF37]">{data.period.start}</span> até <span className="font-mono-tab text-[#D4AF37]">{data.period.end}</span>
            </span>
          </div>

          {/* ================================================
              RESULTADO CONSOLIDADO — hero card
              ================================================ */}
          <div
            className={`fc-card p-5 sm:p-6 mb-6 relative overflow-hidden border`}
            style={{
              background: lucroSign === "positive"
                ? "linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(4,60,40,0.04) 65%, rgba(0,0,0,0) 100%)"
                : lucroSign === "negative"
                  ? "linear-gradient(135deg, rgba(239,68,68,0.14) 0%, rgba(80,10,10,0.05) 60%, rgba(0,0,0,0) 100%)"
                  : "linear-gradient(135deg, rgba(120,120,120,0.06) 0%, rgba(0,0,0,0) 100%)",
              borderColor: lucroSign === "positive" ? "rgba(16,185,129,0.40)" : lucroSign === "negative" ? "rgba(239,68,68,0.45)" : "rgba(80,80,80,0.35)",
            }}
            data-testid="stmt-resultado"
          >
            <div className="grid lg:grid-cols-3 gap-6 items-end">
              <div className="lg:col-span-2">
                <div className={`text-[11px] uppercase tracking-[0.22em] font-semibold flex items-center gap-2 ${lucroSign === "positive" ? "text-emerald-300" : lucroSign === "negative" ? "text-red-300" : "text-[#A3A3A3]"}`}>
                  <Landmark size={13}/> Resultado líquido do período
                </div>
                <div className={`font-display font-bold tracking-tight mt-2 text-[38px] sm:text-[46px] lg:text-[54px] leading-none ${lucroSign === "positive" ? "text-emerald-200" : lucroSign === "negative" ? "text-red-200" : "text-[#E5E5E5]"}`}>
                  {formatBRL(data.lucro_liquido)}
                </div>
                <div className="text-[12px] text-[#A3A3A3] mt-2.5 flex items-center gap-2">
                  {lucroSign === "positive" && <ArrowUpRight size={13} className="text-emerald-300"/>}
                  {lucroSign === "negative" && <ArrowDownRight size={13} className="text-red-300"/>}
                  {lucroSign === "zero" && <Minus size={13} className="text-[#A3A3A3]"/>}
                  Juros recebidos ({formatBRL(data.juros_recebidos)}) − Comissões pagas ({formatBRL(data.comissoes_pagas)})
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-black/30 rounded-md p-3 border border-emerald-500/25">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300/80">Receitas</div>
                  <div className="font-mono-tab text-[15px] text-emerald-200 mt-1 font-semibold">{formatBRL(data.juros_recebidos)}</div>
                </div>
                <div className="bg-black/30 rounded-md p-3 border border-amber-500/25">
                  <div className="text-[10px] uppercase tracking-wider text-amber-300/80">Despesas</div>
                  <div className="font-mono-tab text-[15px] text-amber-200 mt-1 font-semibold">{formatBRL(data.comissoes_pagas)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ================================================
              1) RECEITAS DO PERÍODO
              ================================================ */}
          <StmtSection
            title="1. Receitas do período"
            subtotal={formatBRL(data.juros_recebidos)}
            subtotalAccent="emerald"
            subtotalIcon={TrendingUp}
            testId="stmt-receitas"
          >
            <StmtRow
              label="Juros efetivamente recebidos"
              value={formatBRL(data.juros_recebidos)}
              icon={Coins}
              accent="emerald"
              hint="Pagamentos dentro do período"
              indent={1}
              testId="stmt-row-juros-recebidos"
            />
            <StmtRow
              label="Juros pendentes (venceram no período)"
              value={formatBRL(data.juros_pendentes)}
              icon={TrendingDown}
              accent="amber"
              hint="Aguardando pagamento"
              indent={1}
              testId="stmt-row-juros-pendentes"
            />
          </StmtSection>

          {/* ================================================
              2) MOVIMENTO DE CAPITAL
              ================================================ */}
          <StmtSection
            title="2. Movimento de capital"
            subtotal={`+${data.contratos_novos} / -${data.contratos_quitados}`}
            subtotalAccent="cyan"
            subtotalIcon={Wallet}
            testId="stmt-capital"
          >
            <StmtRow
              label="Capital liberado (saída)"
              value={formatBRL(data.capital_liberado)}
              icon={ArrowDownRight}
              accent="cyan"
              hint={`${data.contratos_novos} novo(s) contrato(s)`}
              indent={1}
              testId="stmt-row-capital-liberado"
            />
            <StmtRow
              label="Capital quitado (entrada)"
              value={formatBRL(data.capital_quitado)}
              icon={ArrowUpRight}
              accent="emerald"
              hint={`${data.contratos_quitados} contrato(s)`}
              indent={1}
              testId="stmt-row-capital-quitado"
            />
            <StmtRow
              label="Capital total recebido"
              value={formatBRL(data.capital_recebido)}
              icon={Coins}
              accent="neutral"
              hint="Principal recuperado no período"
              indent={1}
              testId="stmt-row-capital-recebido"
            />
            <StmtRow
              label="Renovações realizadas"
              value={String(data.renovacoes)}
              icon={RotateCcw}
              accent="gold"
              indent={1}
              testId="stmt-row-renovacoes"
            />
          </StmtSection>

          {/* ================================================
              3) INADIMPLÊNCIA & RECUPERAÇÃO
              ================================================ */}
          <StmtSection
            title="3. Inadimplência e recuperação"
            subtotal={formatBRL(data.inadimplencia_periodo)}
            subtotalAccent="red"
            subtotalIcon={AlertTriangle}
            testId="stmt-inadimplencia"
          >
            <StmtRow
              label="Inadimplência gerada no período"
              value={formatBRL(data.inadimplencia_periodo)}
              icon={AlertTriangle}
              accent="red"
              hint="Vencidos no período ainda em aberto"
              indent={1}
              testId="stmt-row-inadimplencia"
            />
            <StmtRow
              label={`Recuperados no período (${data.recuperados_count} contrato(s))`}
              value={formatBRL(data.recuperados_valor)}
              icon={LifeBuoy}
              accent="emerald"
              indent={1}
              testId="stmt-row-recuperados"
            />
          </StmtSection>

          {/* ================================================
              4) DESPESAS
              ================================================ */}
          <StmtSection
            title="4. Despesas do período"
            subtotal={formatBRL(data.comissoes_pagas)}
            subtotalAccent="amber"
            subtotalIcon={Percent}
            testId="stmt-despesas"
          >
            <StmtRow
              label="Comissões pagas a indicadores"
              value={formatBRL(data.comissoes_pagas)}
              icon={Percent}
              accent="amber"
              indent={1}
              testId="stmt-row-comissoes"
            />
          </StmtSection>

          {/* ================================================
              5) LUCRO LÍQUIDO FINAL (linha grande)
              ================================================ */}
          <div
            className={`rounded-md border p-4 mb-6 flex items-center justify-between ${
              lucroSign === "positive" ? "border-emerald-500/40 bg-emerald-500/5" :
              lucroSign === "negative" ? "border-red-500/40 bg-red-500/5" :
              "border-[#262626] bg-[#0d0d0d]"
            }`}
            data-testid="stmt-lucro-final"
          >
            <div className={`text-[13px] uppercase tracking-[0.18em] font-semibold ${
              lucroSign === "positive" ? "text-emerald-300" :
              lucroSign === "negative" ? "text-red-300" :
              "text-[#A3A3A3]"
            }`}>
              = Lucro líquido do período
            </div>
            <div className={`font-mono-tab text-2xl font-bold ${
              lucroSign === "positive" ? "text-emerald-200" :
              lucroSign === "negative" ? "text-red-200" :
              "text-[#E5E5E5]"
            }`}>
              {formatBRL(data.lucro_liquido)}
            </div>
          </div>

          <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] p-4 text-[11.5px] text-[#737373]" data-testid="fechamento-nota">
            <strong className="text-[#A3A3A3]">Nota:</strong> Este demonstrativo mostra APENAS eventos ocorridos dentro do período.
            Para números atuais de <em>carteira total</em> (capital na rua, exposição total, saldo em recuperação),
            use o Dashboard Executivo, Tesouraria e Recuperação.
          </div>
        </>
      )}

      {loading && !data && (
        <div className="text-[13px] text-[#737373] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin"/> Carregando…
        </div>
      )}
    </Layout>
  );
}
